import { useRef, useState } from 'react';
import { ArrowUpRight, Boxes, BriefcaseBusiness, Factory, Layers3 } from 'lucide-react';
import { Link } from 'react-router-dom';

const platforms = {
  Infor: [
    {
      name: 'Infor LN',
      description: 'ERP capabilities for complex, global manufacturing and service organizations.',
      href: '/services/infor/ln',
      Icon: Factory,
    },
    {
      name: 'Infor CloudSuite Industrial (CSI)',
      description: 'Industry-focused ERP for manufacturers managing production and operations.',
      href: '/services/infor/csi',
      Icon: Boxes,
    },
    {
      name: 'Infor M3',
      description: 'Enterprise ERP for process, distribution, and discrete manufacturing businesses.',
      href: '/services/infor/m3',
      Icon: Layers3,
    },
    {
      name: 'Infor MES',
      description: 'Manufacturing execution capabilities to connect production activities and insight.',
      href: '/services/infor/mes',
      Icon: Factory,
    },
    {
      name: 'Infor WMS',
      description: 'Warehouse management to support inventory visibility and fulfillment operations.',
      href: '/services/infor/wms',
      Icon: Boxes,
    },
    {
      name: 'Infor OS',
      description: 'A platform for connecting Infor applications, data, and workflows.',
      href: '/services/infor/os',
      Icon: Layers3,
    },
  ],
  SAP: [
    {
      name: 'SAP S/4HANA',
      description: 'Modern ERP capabilities to help manage connected business operations.',
      href: '/services/sap',
      Icon: Layers3,
    },
    {
      name: 'SAP ECC',
      description: 'Support for established SAP ERP environments and their ongoing evolution.',
      href: '/services/sap',
      Icon: Boxes,
    },
    {
      name: 'SAP Business Technology Platform',
      description: 'Technology platform capabilities for integration, extension, and innovation.',
      href: '/services/sap',
      Icon: BriefcaseBusiness,
    },
  ],
  Odoo: [
    {
      name: 'Odoo CRM',
      description: 'Manage leads, customer relationships, and sales activities in one place.',
      href: '/services/odoo',
      Icon: BriefcaseBusiness,
    },
    {
      name: 'Odoo Accounting',
      description: 'Bring accounting workflows and financial visibility into your business system.',
      href: '/services/odoo',
      Icon: Layers3,
    },
    {
      name: 'Odoo Inventory',
      description: 'Coordinate inventory operations and maintain clearer stock visibility.',
      href: '/services/odoo',
      Icon: Boxes,
    },
    {
      name: 'Odoo Manufacturing',
      description: 'Connect manufacturing workflows with the rest of your business operations.',
      href: '/services/odoo',
      Icon: Factory,
    },
    {
      name: 'Odoo Project Management',
      description: 'Organize projects, tasks, and team workflows in an integrated workspace.',
      href: '/services/odoo',
      Icon: BriefcaseBusiness,
    },
  ],
};

const platformNames = Object.keys(platforms);

export default function ERPPlatforms() {
  const [activePlatform, setActivePlatform] = useState(platformNames[0]);
  const [activePage, setActivePage] = useState(0);
  const tabRefs = useRef([]);
  const activeIndex = platformNames.indexOf(activePlatform);
  const productsPerPage = 4;
  const currentProducts = platforms[activePlatform];
  const pageCount = Math.ceil(currentProducts.length / productsPerPage);
  const visibleProducts = currentProducts.slice(
    activePage * productsPerPage,
    (activePage + 1) * productsPerPage,
  );

  const selectPlatform = (platform) => {
    setActivePlatform(platform);
    setActivePage(0);
  };

  const handleTabKeyDown = (event) => {
    let nextIndex = activeIndex;
    if (event.key === 'ArrowRight') nextIndex = (activeIndex + 1) % platformNames.length;
    else if (event.key === 'ArrowLeft') nextIndex = (activeIndex - 1 + platformNames.length) % platformNames.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = platformNames.length - 1;
    else return;

    event.preventDefault();
    selectPlatform(platformNames[nextIndex]);
    tabRefs.current[nextIndex]?.focus();
  };

  return (
    <section
      className="relative isolate overflow-hidden px-6 py-14 text-white sm:px-8 sm:py-16 lg:px-[6.75vw]"
      style={{
        backgroundImage: 'linear-gradient(110deg, #17104c 0%, #1455a7 100%)',
      }}
      aria-labelledby="erp-platforms-title"
    >
      <div className="mx-auto w-full">
        <h2 id="erp-platforms-title" className="text-center text-white text-[32px] font-semibold leading-[48px]">
          ERP Platforms and Expertise
        </h2>
        <p className="mx-auto mt-4 max-w-7xl text-center text-white/90 text-base font-normal leading-[27px]">
          PT KCS helps businesses select, implement, and modernize ERP platforms—from established enterprise systems to flexible solutions for growing SMEs.
        </p>

        <div
          className="mx-auto mt-8 flex w-full max-w-2xl border-b border-white/40"
          role="tablist"
          aria-label="ERP platforms"
          onKeyDown={handleTabKeyDown}
        >
          {platformNames.map((platform, index) => (
            <button
              key={platform}
              ref={(element) => { tabRefs.current[index] = element; }}
              id={`erp-platform-tab-${platform.toLowerCase()}`}
              type="button"
              role="tab"
              aria-selected={activePlatform === platform}
              aria-controls="erp-platform-panel"
              tabIndex={activePlatform === platform ? 0 : -1}
              onClick={() => selectPlatform(platform)}
              className={`flex-1 border-b-2 px-5 py-3 text-center text-base font-medium transition-colors sm:text-lg ${
                activePlatform === platform
                  ? 'border-sky-300 text-white'
                  : 'border-transparent text-white/75 hover:text-white'
              }`}
            >
              {platform}
            </button>
          ))}
        </div>

        <div
          id="erp-platform-panel"
          className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4"
          role="tabpanel"
          aria-labelledby={`erp-platform-tab-${activePlatform.toLowerCase()}`}
          tabIndex={0}
        >
          {visibleProducts.map(({ name, description, href, Icon }) => (
            <article
              key={name}
              className="flex min-h-[250px] flex-col rounded-lg p-5 text-center shadow-lg sm:p-6"
              style={{ backgroundColor: '#ffffff', color: '#292929' }}
            >
              <div className="mx-auto mb-3 grid h-16 place-items-center" style={{ color: '#1455a7' }} aria-hidden="true">
                <Icon size={56} strokeWidth={1.7} />
              </div>
              <h3 className="text-lg font-semibold leading-snug" style={{ color: '#183762' }}>
                {name}
              </h3>
              <p className="mt-3 flex-1 text-base font-normal leading-[27px]" style={{ color: '#212121',wordBreak: 'normal',overflowWrap: 'normal',hyphens: 'none',whieSpace: 'normal' }}>
                {description}
              </p>
              {/* <Link
                to={href}
                onClick={() => window.scrollTo(0, 0)}
                className="mt-5 inline-flex items-center justify-center gap-1 text-sm font-semibold"
                style={{ color: '#087fc1' }}
              >
                Learn more <ArrowUpRight size={16} aria-hidden="true" />
              </Link> */}
            </article>
          ))}
        </div>
        {pageCount > 1 && (
          <div className="mt-7 flex justify-center gap-2" aria-label={`${activePlatform} product pages`}>
            {Array.from({ length: pageCount }, (_, pageIndex) => (
              <button
                key={pageIndex}
                type="button"
                aria-label={`Show ${activePlatform} products page ${pageIndex + 1}`}
                aria-current={activePage === pageIndex ? 'page' : undefined}
                onClick={() => setActivePage(pageIndex)}
                className="h-3 w-3 rounded-full transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                style={{ backgroundColor: activePage === pageIndex ? '#31b7ed' : '#ffffff' }}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
